import { CloseOutlined } from '@mui/icons-material';
import { Button, Dropdown } from 'finara-components';
import type { DropdownOption } from 'finara-components';
import React, { useState, useEffect } from 'react';

import { Input } from '@/components/common/Input';
import { useDepartments } from '@/hooks/queries/useDepartments';
import { useUserJobTitles } from '@/hooks/queries/useJobTitles';
import { useUserSalaries } from '@/hooks/queries/useSalaries';
import type { EmployeeData } from '@/types/analytics';
import type { UpdateUserData } from '@/types/user';

interface EditEmployeeModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (
    userData: UpdateUserData,
    salaryData?: {
      salaryId?: string;
      amount: number;
      type: string;
      currency?: string;
      frequency?: string;
    },
    jobTitleData?: {
      jobTitleId?: string;
      name: string;
      description?: string;
    }
  ) => void;
  employee: EmployeeData | null;
  isLoading?: boolean;
  /** Super-admin: scope the department list to the target organization. */
  organizationId?: string;
}

export const EditEmployeeModal: React.FC<EditEmployeeModalProps> = ({
  isOpen,
  onClose,
  onSubmit,
  employee,
  isLoading = false,
  organizationId,
}) => {
  const [formData, setFormData] = useState<UpdateUserData>({
    name: '',
    email: '',
    departmentId: '',
    status: 'active',
  });

  const [salaryAmount, setSalaryAmount] = useState('');
  const [salaryCurrency, setSalaryCurrency] = useState('MXN');
  const [salaryFrequency, setSalaryFrequency] = useState('monthly');

  const [jobTitleName, setJobTitleName] = useState('');
  const [jobTitleDescription, setJobTitleDescription] = useState('');

  const [errors, setErrors] = useState<Record<string, string>>({});

  // Fetch departments, salaries, and job titles
  const { data: departmentsData } = useDepartments(
    organizationId ? { organizationId } : undefined
  );
  const { data: salariesData } = useUserSalaries(employee?.id);
  const { data: jobTitlesData } = useUserJobTitles(employee?.id);

  // Build dropdown options
  const departmentOptions: DropdownOption[] = React.useMemo(() => {
    const options: DropdownOption[] = [{ value: '', label: 'Sin departamento' }];
    if (departmentsData?.data) {
      departmentsData.data.forEach(dept => {
        options.push({ value: dept.id, label: dept.name });
      });
    }
    return options;
  }, [departmentsData]);

  const statusOptions: DropdownOption[] = [
    { value: 'active', label: 'Activo' },
    { value: 'inactive', label: 'Inactivo' },
  ];

  const currencyOptions: DropdownOption[] = [
    { value: 'MXN', label: 'MXN (Peso Mexicano)' },
    { value: 'USD', label: 'USD (Dólar)' },
    { value: 'EUR', label: 'EUR (Euro)' },
  ];

  const frequencyOptions: DropdownOption[] = [
    { value: 'monthly', label: 'Mensual' },
    { value: 'biweekly', label: 'Quincenal' },
  ];

  // Initialize form data when employee changes
  useEffect(() => {
    if (employee) {
      // Find department ID by name (since API returns name, not ID)
      const department = departmentsData?.data.find(d => d.name === employee.department);

      setFormData({
        name: employee.name,
        email: employee.email,
        departmentId: department?.id || '',
        status: employee.isActive ? 'active' : 'inactive',
      });

      // Set salary data
      if (salariesData && salariesData.length > 0) {
        const salary = salariesData[0];
        if (salary) {
          setSalaryAmount(salary.amount.toString());
          setSalaryCurrency(salary.currency);
          setSalaryFrequency(salary.frequency);
        }
      } else {
        // Reset to defaults for new salary
        setSalaryAmount('');
        setSalaryCurrency('MXN');
        setSalaryFrequency('monthly');
      }

      // Set job title data
      if (jobTitlesData && jobTitlesData.length > 0) {
        const jobTitle = jobTitlesData[0];
        if (jobTitle) {
          setJobTitleName(jobTitle.name);
          setJobTitleDescription(jobTitle.description || '');
        }
      } else {
        // Reset for new job title
        setJobTitleName('');
        setJobTitleDescription('');
      }
    }
  }, [employee, departmentsData, salariesData, jobTitlesData]);

  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {};

    if (formData.name && !formData.name.trim()) {
      newErrors['name'] = 'El nombre no puede estar vacío';
    }

    if (formData.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors['email'] = 'Email inválido';
    }

    if (salaryAmount) {
      const amount = parseFloat(salaryAmount);
      if (isNaN(amount) || amount <= 0) {
        newErrors['salary'] = 'El salario debe ser un número positivo';
      }
    }

    if (jobTitleName && !jobTitleName.trim()) {
      newErrors['jobTitle'] = 'El nombre del puesto no puede estar vacío';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) return;

    // Only send fields that have been changed
    const submitData: UpdateUserData = {};

    if (formData.name && formData.name !== employee?.name) {
      submitData.name = formData.name;
    }

    if (formData.email && formData.email !== employee?.email) {
      submitData.email = formData.email;
    }

    if (formData.departmentId) {
      submitData.departmentId = formData.departmentId;
    }

    if (formData.status) {
      submitData.status = formData.status;
    }

    // Prepare salary data if changed or creating new
    let salaryUpdateData:
      | { salaryId?: string; amount: number; type: string; currency?: string; frequency?: string }
      | undefined;

    const hasSalary = salariesData && salariesData.length > 0;

    if (salaryAmount) {
      const newAmount = parseFloat(salaryAmount);

      if (hasSalary) {
        // Update existing salary
        const currentSalary = salariesData[0];
        if (currentSalary && newAmount !== currentSalary.amount) {
          salaryUpdateData = {
            salaryId: currentSalary.id,
            amount: newAmount,
            type: 'base', // Always base
          };
        }
      } else {
        // Create new salary
        salaryUpdateData = {
          amount: newAmount,
          type: 'base', // Always base
          currency: salaryCurrency,
          frequency: salaryFrequency,
        };
      }
    }

    // Prepare job title data if changed or creating new
    let jobTitleUpdateData: { jobTitleId?: string; name: string; description?: string } | undefined;

    const hasJobTitle = jobTitlesData && jobTitlesData.length > 0;

    if (jobTitleName.trim()) {
      if (hasJobTitle && jobTitlesData[0]) {
        // Update existing job title
        const currentJobTitle = jobTitlesData[0];
        if (
          jobTitleName !== currentJobTitle.name ||
          jobTitleDescription !== (currentJobTitle.description || '')
        ) {
          jobTitleUpdateData = {
            jobTitleId: currentJobTitle.id,
            name: jobTitleName.trim(),
            description: jobTitleDescription.trim() || undefined,
          };
        }
      } else {
        // Create new job title
        jobTitleUpdateData = {
          name: jobTitleName.trim(),
          description: jobTitleDescription.trim() || undefined,
        };
      }
    }

    onSubmit(submitData, salaryUpdateData, jobTitleUpdateData);
  };

  const handleClose = () => {
    setFormData({
      name: '',
      email: '',
      departmentId: '',
      status: 'active',
    });
    setSalaryAmount('');
    setSalaryCurrency('MXN');
    setSalaryFrequency('monthly');
    setJobTitleName('');
    setJobTitleDescription('');
    setErrors({});
    onClose();
  };

  if (!isOpen || !employee) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
        {/* Backdrop */}
        <button
          className="absolute inset-0 bg-black opacity-60 cursor-default"
          onClick={handleClose}
          type="button"
          onKeyDown={e => {
            if (e.key === 'Escape') handleClose();
          }}
          aria-label="Cerrar modal"
        />

        {/* Modal */}
        <div className="relative bg-surface border border-black/10 dark:border-white/15 rounded-lg shadow-2xl shadow-black/60 max-w-2xl w-full max-h-[90vh] overflow-y-auto">
          {/* Header */}
          <div className="flex items-center justify-between p-6 border-b border-border">
            <h2 className="text-xl font-semibold text-brand-white">Editar Empleado</h2>
            <button
              onClick={handleClose}
              className="p-2 text-muted hover:text-brand-white rounded-lg hover:bg-black/5 dark:hover:bg-white/5 transition-colors"
              disabled={isLoading}
            >
              <CloseOutlined />
            </button>
          </div>

          {/* Body */}
          <form onSubmit={handleSubmit} className="p-6 space-y-6">
            {/* Basic Information */}
            <div>
              <h3 className="text-sm font-semibold text-brand-white mb-4">Información Básica</h3>
              <div className="space-y-4">
                <div>
                  <Input
                    id="name"
                    label="Nombre Completo"
                    type="text"
                    value={formData.name}
                    onChange={e => setFormData(prev => ({ ...prev, name: e.target.value }))}
                    placeholder="Ej: Juan Pérez García"
                  />
                  {errors['name'] && <p className="text-sm text-red-600 mt-1">{errors['name']}</p>}
                </div>

                <div>
                  <Input
                    id="email"
                    label="Email"
                    type="email"
                    value={formData.email}
                    onChange={e => setFormData(prev => ({ ...prev, email: e.target.value }))}
                    placeholder="Ej: juan.perez@empresa.com"
                  />
                  {errors['email'] && (
                    <p className="text-sm text-red-600 mt-1">{errors['email']}</p>
                  )}
                </div>
              </div>
            </div>

            {/* Department and Status */}
            <div>
              <h3 className="text-sm font-semibold text-brand-white mb-4">Departamento y Estado</h3>
              <div className="space-y-4">
                <div>
                  <label
                    htmlFor="departmentId"
                    className="block text-sm font-medium text-brand-white mb-1"
                  >
                    Departamento
                  </label>
                  <Dropdown
                    id="departmentId"
                    value={formData.departmentId || ''}
                    onChange={value =>
                      setFormData(prev => ({ ...prev, departmentId: String(value) }))
                    }
                    options={departmentOptions}
                    placeholder="Selecciona un departamento"
                    searchable
                  />
                </div>

                <div>
                  <label htmlFor="status" className="block text-sm font-medium text-brand-white mb-1">
                    Estado
                  </label>
                  <Dropdown
                    id="status"
                    value={formData.status || 'active'}
                    onChange={value => setFormData(prev => ({ ...prev, status: String(value) }))}
                    options={statusOptions}
                    placeholder="Selecciona un estado"
                  />
                </div>
              </div>
            </div>

            {/* Job Title Information */}
            <div>
              <h3 className="text-sm font-semibold text-brand-white mb-4">
                {jobTitlesData && jobTitlesData.length > 0
                  ? 'Puesto de Trabajo'
                  : 'Crear Puesto de Trabajo'}
              </h3>
              <div className="space-y-4">
                <div>
                  <Input
                    id="job-title-name"
                    label="Nombre del Puesto"
                    type="text"
                    value={jobTitleName}
                    onChange={e => {
                      setJobTitleName(e.target.value);
                      if (errors['jobTitle']) {
                        setErrors(prev => {
                          const newErrors = { ...prev };
                          delete newErrors['jobTitle'];
                          return newErrors;
                        });
                      }
                    }}
                    placeholder="Ej: Gerente de Ventas, Desarrollador Senior"
                  />
                  {errors['jobTitle'] && (
                    <p className="text-sm text-red-600 mt-1">{errors['jobTitle']}</p>
                  )}
                </div>

                <div>
                  <label
                    htmlFor="job-title-description"
                    className="mb-1 block text-sm font-medium text-brand-white"
                  >
                    Descripción del Puesto (Opcional)
                  </label>
                  <textarea
                    id="job-title-description"
                    value={jobTitleDescription}
                    onChange={e => setJobTitleDescription(e.target.value)}
                    placeholder="Describe las responsabilidades y funciones del puesto..."
                    rows={3}
                    className="w-full rounded-lg border border-border bg-surface text-brand-white placeholder:text-muted px-4 py-2 text-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
                  />
                </div>
              </div>
            </div>

            {/* Salary Information */}
            <div>
              <h3 className="text-sm font-semibold text-brand-white mb-4">
                {salariesData && salariesData.length > 0
                  ? 'Información Salarial'
                  : 'Crear Información Salarial'}
              </h3>
              <div className="space-y-4">
                {salariesData && salariesData.length > 0 && salariesData[0] ? (
                  // Edit mode - show read-only currency and frequency
                  <div className="rounded-lg bg-black/10 dark:bg-white/5 p-4">
                    <div className="grid grid-cols-2 gap-4 text-sm">
                      <div>
                        <p className="text-muted">Moneda:</p>
                        <p className="font-semibold text-brand-white">{salariesData[0].currency}</p>
                      </div>
                      <div>
                        <p className="text-muted">Frecuencia:</p>
                        <p className="font-semibold text-brand-white">
                          {salariesData[0].frequency === 'monthly' ? 'Mensual' : 'Quincenal'}
                        </p>
                      </div>
                    </div>
                  </div>
                ) : (
                  // Create mode - show editable currency and frequency dropdowns
                  <>
                    <div>
                      <label
                        htmlFor="salary-currency"
                        className="block text-sm font-medium text-brand-white mb-1"
                      >
                        Moneda
                      </label>
                      <Dropdown
                        id="salary-currency"
                        value={salaryCurrency}
                        onChange={value => setSalaryCurrency(String(value))}
                        options={currencyOptions}
                        placeholder="Selecciona moneda"
                      />
                    </div>

                    <div>
                      <label
                        htmlFor="salary-frequency"
                        className="block text-sm font-medium text-brand-white mb-1"
                      >
                        Frecuencia de Pago
                      </label>
                      <Dropdown
                        id="salary-frequency"
                        value={salaryFrequency}
                        onChange={value => setSalaryFrequency(String(value))}
                        options={frequencyOptions}
                        placeholder="Selecciona frecuencia"
                      />
                    </div>
                  </>
                )}

                <div>
                  <Input
                    id="salary-amount"
                    label="Monto del Salario"
                    type="number"
                    step="0.01"
                    value={salaryAmount}
                    onChange={e => {
                      setSalaryAmount(e.target.value);
                      if (errors['salary']) {
                        setErrors(prev => {
                          const newErrors = { ...prev };
                          delete newErrors['salary'];
                          return newErrors;
                        });
                      }
                    }}
                    placeholder="0.00"
                  />
                  {errors['salary'] && (
                    <p className="text-sm text-red-600 mt-1">{errors['salary']}</p>
                  )}
                </div>
              </div>
            </div>

            {/* Footer */}
            <div className="flex items-center justify-end gap-3 pt-6 border-t border-border">
              <Button variant="white" onClick={handleClose} disabled={isLoading}>
                Cancelar
              </Button>
              <Button type="submit" variant="black" disabled={isLoading}>
                {isLoading ? 'Guardando...' : 'Guardar Cambios'}
              </Button>
            </div>
          </form>
        </div>
    </div>
  );
};
